import * as React from 'react';
import { CollapsibleCard, cn } from '@insolech/ui';
import type {
  RearfootState,
  HeelRaiseType,
  HeelRaiseLength,
  SubcalOffType,
  SubcalOffFill,
  SubcalOffPosition,
  HeelCutoutDimension,
  SubcalcanealOffloadingSide,
  RearfootWedgeType,
  RearfootWedgeCorrection,
} from './prescriptionState';
import { ModCard, SideRow, SelectSm, NumInput, SectionObservation } from './shared';
import { CARD_IDS } from './cardIds';
import {
  rearfootWedgeTypesFor,
  isRearfootPostType,
  normalizeRearfootWedgeType,
  REARFOOT_POST_ANGLE,
  REARFOOT_SKIVE_ANGLE,
} from './rearfootWedgeRules';
import {
  heelRaiseLengthsFor,
  heelRaiseTypesFor,
  subcalOffloadingTypesFor,
} from './heelRaiseRules';
import type { UnavailableLookup } from '../prescriptionLegality';

/**
 * RearfootSection.
 *
 * Cards (in this order):
 *   1. Subcalcaneus offloading (full width - complex per-side fields)
 *   2. Heel raise (2/3 width)
 *   3. Rearfoot posting (1/3 width)
 *   4. Rearfoot wedge (full width - unified intrinsic / extrinsic per side)
 *
 * Default seeded on mount (only one):
 *   - Subcalcaneal offloading L+R → heel_offloading + carbosan_3mm + -3mm
 * Every other modification card starts empty; the operator opts in.
 */

interface Props {
  value: RearfootState;
  onChange: (next: RearfootState) => void;
  subtype?: string | null | undefined;
  /** Sandal-only branch (R37). When true, the section hides Rearfoot
   *  posting entirely, prunes option lists (heel_cutout, extrinsic heel
   *  raise, EVA-post rearfoot wedge), and trims the verbose subtitles
   *  per Rafael's sandal-form spec. */
  isSandal?: boolean | undefined;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  /** Controlled open - when provided, the wizard's prescription assistant
   *  can force-open this section before scrolling to a card inside it. */
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
  /** Values this product does not offer, keyed by the checker's field key, so a
   *  control can mark ITSELF as the one the red banner is complaining about.
   *  See the indexUnavailable comment in ../prescriptionLegality for why the
   *  section is told rather than left to work it out from its own options. */
  unavailable?: UnavailableLookup | undefined;
}

// ---- Constants -----------------------------------------------------------

export const SUBCAL_OFFLOADING_TYPES: ReadonlyArray<{ value: SubcalOffType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'heel_offloading', label: 'Heel offloading' },
  { value: 'heel_cutout', label: 'Heel cutout' },
];

export const SUBCAL_FILL_OPTIONS: ReadonlyArray<{ value: SubcalOffFill; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'carbosan_3mm', label: 'With 3mm carbosan' },
  { value: 'poron_3mm', label: 'With 3mm poron' },
];

export const SUBCAL_POSITION_OPTIONS: ReadonlyArray<{ value: SubcalOffPosition; label: string }> = [
  { value: 'center', label: 'Center' },
  { value: 'medial', label: 'Medial' },
  { value: 'lateral', label: 'Lateral' },
];

export const HEEL_CUTOUT_DIMENSIONS: ReadonlyArray<{ value: HeelCutoutDimension; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const HEEL_RAISE_TYPES: ReadonlyArray<{ value: HeelRaiseType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'intrinsic', label: 'Intrinsic' },
  { value: 'extrinsic', label: 'Extrinsic' },
];

export const HEEL_RAISE_LENGTHS: ReadonlyArray<{ value: HeelRaiseLength; label: string }> = [
  { value: 'short', label: 'Short (calcaneus area)' },
  { value: 'long', label: 'Long (to MTT area)' },
];

// Rearfoot wedge - type drives the manufacturing approach (built into the
// shell vs glued under it). R34 splits intrinsic into two variants
// (Kirby skive vs Rearfoot post) so the lab knows which one to build.
export const REARFOOT_WEDGE_TYPES: ReadonlyArray<{ value: RearfootWedgeType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'kirby_skive', label: 'Kirby Skive (intrinsic)' },
  { value: 'rearfoot_post_intrinsic', label: 'Rearfoot post (intrinsic)' },
  { value: 'rearfoot_eva_post_extrinsic', label: 'Rearfoot EVA post (extrinsic)' },
];

export const REARFOOT_WEDGE_CORRECTIONS: ReadonlyArray<{
  value: RearfootWedgeCorrection;
  label: string;
}> = [
  { value: 'medial', label: 'Medial heel skive (supinating wedge)' },
  { value: 'lateral', label: 'Lateral heel skive (pronating wedge)' },
];

// Subcalcaneal default applied on first mount for both sides.
// Position only applies to heel_cutout, so it's omitted here.
const DEFAULT_SUBCAL_SIDE: SubcalcanealOffloadingSide = {
  type: 'heel_offloading',
  fill: 'carbosan_3mm',
  depth_mm: -3,
};

// ---- Component -----------------------------------------------------------

export function RearfootSection({
  value,
  onChange,
  subtype,
  isSandal = false,
  readOnly,
  defaultOpen = true,
  open,
  onOpenChange,
  unavailable,
}: Props) {
  // Single-write helper (matches the pattern in InsoleDesignSection - avoids
  // multi-set batching bugs).
  const patch = React.useCallback(
    (p: Partial<RearfootState>) => onChange({ ...value, ...p }),
    [onChange, value],
  );

  // Seed defaults on first mount:
  //   - Subcalcaneal offloading both sides (heel offloading + 3mm carbosan + -3mm).
  //   - Rearfoot posting both sides = Yes (the common case; admin can untick).
  // Every other card stays empty until the operator picks something.
  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const updates: Partial<RearfootState> = {};
    if (!value.subcalcaneal_offloading) {
      updates.subcalcaneal_offloading = {
        left: { ...DEFAULT_SUBCAL_SIDE },
        right: { ...DEFAULT_SUBCAL_SIDE },
      };
    }
    if (
      !value.rearfoot_posting ||
      (value.rearfoot_posting.left_enabled == null &&
        value.rearfoot_posting.right_enabled == null)
    ) {
      updates.rearfoot_posting = {
        ...(value.rearfoot_posting ?? {}),
        left_enabled: true,
        right_enabled: true,
      };
    }
    if (Object.keys(updates).length > 0) {
      onChange({ ...value, ...updates });
    }
    // Mount-only seeding guarded by initialized.current ref; including
    // `value`/`onChange` would re-run on every parent render and clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // R38 — heel-raise availability by shell: carbon/fibreglass = extrinsic only
  // (hide intrinsic); EVA/TPU = intrinsic only (hide extrinsic); PA12 &
  // polypropylene = both. Sandals hide extrinsic.
  //
  // The rule itself now lives in ./heelRaiseRules so the legality checker can
  // ask the same question this card asks. The two flags below drive the
  // subtitle copy only; the option list comes from the shared rule.
  const heelRaiseHideIntrinsic =
    subtype === 'carbon_insole' || subtype === 'fibreglass_insole';
  const heelRaiseHideExtrinsic =
    subtype === 'eva_insole' || subtype === 'tpu_insole';
  const heelRaiseTypeOptions = React.useMemo(() => {
    const allowed = heelRaiseTypesFor(subtype ?? null, !!isSandal);
    return HEEL_RAISE_TYPES.filter((t) => allowed.includes(t.value));
  }, [subtype, isSandal]);

  const subcal = value.subcalcaneal_offloading ?? {};
  const hr = value.heel_raise ?? {};
  const rp = value.rearfoot_posting ?? {};
  const rw = value.rearfoot_wedge ?? {};

  // Controlled when the wizard's prescription assistant hands us an open
  // state; otherwise stay uncontrolled with defaultOpen as today.
  const isControlled = open !== undefined && onOpenChange !== undefined;
  return (
    <CollapsibleCard
      title="Rearfoot modifications"
      subtitle="Subcalcaneal offloading, heel raise, rearfoot posting, rearfoot wedge"
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <div className="space-y-3">
        {/* 1. Subcalcaneus offloading - full width, first card */}
        <SubcalcanealOffloadingCard
          value={subcal}
          onChange={(v) => patch({ subcalcaneal_offloading: v })}
          readOnly={readOnly}
          isSandal={isSandal}
          unavailable={unavailable}
        />

        {/* Row A: Heel raise (2/3) + Rearfoot posting (1/3 - hidden for
            sandals so Heel raise spans the full row). */}
        <div
          className={
            isSandal
              ? 'grid grid-cols-1 gap-3'
              : 'grid grid-cols-1 md:grid-cols-3 gap-3'
          }
        >
          {/* 2. Heel raise - labels removed, fields fit a single row per side.
              Sandal subtitle drops the "Intrinsic = into the shell.
              Extrinsic = EVA post." prefix per R37. */}
          <div className={isSandal ? '' : 'md:col-span-2'}>
          <ModCard
            id={CARD_IDS.heelRaise}
            title="Heel raise"
            subtitle={
              heelRaiseHideIntrinsic
                ? 'Extrinsic only on carbon / fibreglass shells. Height 1-15mm.'
                : heelRaiseHideExtrinsic
                  ? 'Intrinsic only on EVA / TPU shells. Height 1-15mm.'
                  : isSandal
                    ? 'Height 1-15mm.'
                    : 'Intrinsic = into the shell. Extrinsic = EVA post. Height 1-15mm.'
            }
            image={{ src: '/prescription/modifications/heel-raise.png', alt: 'Heel raise reference' }}
            info={
              <div className="space-y-2 max-w-[280px] text-xs leading-snug">
                <p>
                  <strong>Intrinsic</strong> heel raise refers to integrating it
                  into the shell (only short type).
                </p>
                <p>
                  <strong>Extrinsic</strong> refers to an EVA post applied to
                  the bottom of the insole (both short and long).
                </p>
                <p>Height ranges from 1mm to 15mm.</p>
              </div>
            }
          >
            <SideRow side="L">
              <SelectSm
                value={hr.left_type}
                onChange={(v) => {
                  // None / undefined: clear the dependent length + height so
                  // stale data doesn't get submitted. Otherwise apply the
                  // intrinsic auto-snap (intrinsic only supports 'short').
                  if (!v || v === 'none') {
                    patch({
                      heel_raise: {
                        ...hr,
                        left_type: v,
                        left_length: undefined,
                        left_height_mm: undefined,
                      },
                    });
                    return;
                  }
                  const nextLength =
                    v === 'intrinsic' && hr.left_length !== 'short'
                      ? 'short'
                      : hr.left_length;
                  patch({
                    heel_raise: { ...hr, left_type: v, left_length: nextLength },
                  });
                }}
                options={heelRaiseTypeOptions}
                disabled={readOnly}
                placeholder="Type"
                className="min-w-[6rem]"
                unavailableLabel={unavailable?.['heel_raise_left_type']?.valueLabel}
                invalid={!!unavailable?.['heel_raise_left_type']}
              />
              {hr.left_type && hr.left_type !== 'none' ? (
                <>
                  <SelectSm
                    value={hr.left_length}
                    onChange={(v) => patch({ heel_raise: { ...hr, left_length: v } })}
                    options={HEEL_RAISE_LENGTHS.filter((l) =>
                      heelRaiseLengthsFor(hr.left_type, !!isSandal).includes(l.value),
                    )}
                    disabled={readOnly}
                    placeholder="Length"
                    className="min-w-[5rem]"
                    unavailableLabel={unavailable?.['heel_raise_left_length']?.valueLabel}
                    invalid={!!unavailable?.['heel_raise_left_length']}
                  />
                  <NumInput
                    value={hr.left_height_mm}
                    onChange={(v) => patch({ heel_raise: { ...hr, left_height_mm: v } })}
                    min={1}
                    max={15}
                    disabled={readOnly}
                    placeholder="Height"
                    suffix="mm"
                    width="w-24"
                    invalid={hr.left_height_mm == null}
                  />
                  {hr.left_height_mm == null ? (
                    <span className="text-[11px] font-medium text-error-500">Required</span>
                  ) : null}
                </>
              ) : null}
            </SideRow>
            <SideRow side="R">
              <SelectSm
                value={hr.right_type}
                onChange={(v) => {
                  if (!v || v === 'none') {
                    patch({
                      heel_raise: {
                        ...hr,
                        right_type: v,
                        right_length: undefined,
                        right_height_mm: undefined,
                      },
                    });
                    return;
                  }
                  const nextLength =
                    v === 'intrinsic' && hr.right_length !== 'short'
                      ? 'short'
                      : hr.right_length;
                  patch({
                    heel_raise: { ...hr, right_type: v, right_length: nextLength },
                  });
                }}
                options={heelRaiseTypeOptions}
                disabled={readOnly}
                placeholder="Type"
                className="min-w-[6rem]"
                unavailableLabel={unavailable?.['heel_raise_right_type']?.valueLabel}
                invalid={!!unavailable?.['heel_raise_right_type']}
              />
              {hr.right_type && hr.right_type !== 'none' ? (
                <>
                  <SelectSm
                    value={hr.right_length}
                    onChange={(v) => patch({ heel_raise: { ...hr, right_length: v } })}
                    options={HEEL_RAISE_LENGTHS.filter((l) =>
                      heelRaiseLengthsFor(hr.right_type, !!isSandal).includes(l.value),
                    )}
                    disabled={readOnly}
                    placeholder="Length"
                    className="min-w-[5rem]"
                    unavailableLabel={unavailable?.['heel_raise_right_length']?.valueLabel}
                    invalid={!!unavailable?.['heel_raise_right_length']}
                  />
                  <NumInput
                    value={hr.right_height_mm}
                    onChange={(v) => patch({ heel_raise: { ...hr, right_height_mm: v } })}
                    min={1}
                    max={15}
                    disabled={readOnly}
                    placeholder="Height"
                    suffix="mm"
                    width="w-24"
                    invalid={hr.right_height_mm == null}
                  />
                  {hr.right_height_mm == null ? (
                    <span className="text-[11px] font-medium text-error-500">Required</span>
                  ) : null}
                </>
              ) : null}
            </SideRow>
          </ModCard>
          </div>

          {/* 3. Rearfoot posting - L/R rows of Yes/No (no extra label).
              Hidden entirely for sandals (R37). */}
          {!isSandal ? (
            <div className="md:col-span-1">
            <ModCard
              id={CARD_IDS.rearfootPosting}
              title="Rearfoot posting"
              subtitle="Heel post for stabilization."
              image={{ src: '/prescription/modifications/rearfoot-posting.png', alt: 'Rearfoot posting reference' }}
            >
              <SideRow side="L">
                <YesNoToggle
                  value={rp.left_enabled}
                  onChange={(b) => patch({ rearfoot_posting: { ...rp, left_enabled: b } })}
                  disabled={readOnly}
                />
              </SideRow>
              <SideRow side="R">
                <YesNoToggle
                  value={rp.right_enabled}
                  onChange={(b) => patch({ rearfoot_posting: { ...rp, right_enabled: b } })}
                  disabled={readOnly}
                />
              </SideRow>
            </ModCard>
            </div>
          ) : null}
        </div>

        {/* 4. Rearfoot wedge - unified intrinsic / extrinsic per side.
            Sandal subtitle drops the "Both intrinsic Kirby skive or
            Rearfoot post are the recommended options" tail per R37. */}
        <ModCard
          id={CARD_IDS.rearfootWedge}
          title="Rearfoot wedge"
          subtitle={
            isSandal
              ? 'Standard correction: 15° and -3mm depth.'
              : 'Standard correction: 15° and -3mm depth. Both intrinsic Kirby skive or Rearfoot post are the recommended options'
          }
          image={{
            src: '/prescription/modifications/rearfoot-wedge.png',
            alt: 'Rearfoot wedge reference',
          }}
          info={
            <div className="space-y-2 max-w-[280px] text-xs leading-snug">
              <p>
                <strong>Medial heel skive</strong> acts as a supinating wedge.
                <br />
                <strong>Lateral heel skive</strong> acts as a pronating wedge.
              </p>
              <p>
                Typical default: 15° angle, around -3mm depth. Clinicians keep
                the angle constant and tune depth for the correction level.
              </p>
            </div>
          }
        >
          {/* All three wedge types are available per side independent of the
              rearfoot posting flag (the lab can build the EVA post on its own). */}
          <RearfootWedgeRow
            side="L"
            data={rw}
            patch={patch}
            readOnly={readOnly}
            isSandal={isSandal}
            subtype={subtype}
            unavailable={unavailable}
          />
          <RearfootWedgeRow
            side="R"
            data={rw}
            patch={patch}
            readOnly={readOnly}
            isSandal={isSandal}
            subtype={subtype}
            unavailable={unavailable}
          />
        </ModCard>

        <SectionObservation
          value={value.observations}
          onChange={(v) => patch({ observations: v })}
          disabled={readOnly}
          placeholder="Section-specific notes for rearfoot…"
        />
      </div>
    </CollapsibleCard>
  );
}

// ---- Rearfoot wedge row --------------------------------------------------

function RearfootWedgeRow({
  side,
  data,
  patch,
  readOnly,
  isSandal,
  subtype,
  unavailable,
}: {
  side: 'L' | 'R';
  data: NonNullable<RearfootState['rearfoot_wedge']>;
  patch: (p: Partial<RearfootState>) => void;
  readOnly?: boolean | undefined;
  isSandal?: boolean | undefined;
  subtype?: string | null | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  const isLeft = side === 'L';
  const type = normalizeRearfootWedgeType(
    isLeft ? data.left_type : data.right_type,
  );
  // Both post types (extrinsic EVA post + intrinsic rearfoot post) share a
  // 1-10° angle range (default 3°) and hide the separate skive depth. Only the
  // Kirby skive uses the 0-30° angle (default 15°) + a depth field.
  const isPostType = isRearfootPostType(type);
  const hidesDepth = isPostType;
  const correction = isLeft ? data.left_correction : data.right_correction;
  const degrees = isLeft ? data.left_degrees : data.right_degrees;
  const depth = isLeft ? data.left_depth_mm : data.right_depth_mm;

  const update = (changes: Partial<typeof data>) => {
    patch({ rearfoot_wedge: { ...data, ...changes } });
  };

  const setType = (v: RearfootWedgeType | undefined) => {
    // Both post types (extrinsic EVA post + intrinsic rearfoot post) carry no
    // depth (only the Kirby skive seeds the -3mm default) and share the 1-10°
    // angle range (default 3°).
    const isPostTypeT = isRearfootPostType(v);
    // Post types use a 1-10° range with default 3°; the Kirby skive uses 0-30°
    // with default 15°. Clamp existing values outside the post bounds so the
    // input doesn't render out of range.
    const validDegreesFor = (
      existing: number | undefined,
      usesPostAngle: boolean,
    ): number => {
      if (usesPostAngle) {
        // Keep existing only when in the post range; otherwise reset to default.
        return existing != null &&
          existing >= REARFOOT_POST_ANGLE.min &&
          existing <= REARFOOT_POST_ANGLE.max
          ? existing
          : REARFOOT_POST_ANGLE.default;
      }
      // Kirby skive keeps existing if set; otherwise default.
      return existing ?? REARFOOT_SKIVE_ANGLE.default;
    };
    if (isLeft) {
      if (v && v !== 'none') {
        update({
          left_type: v,
          left_correction: data.left_correction ?? 'medial',
          left_degrees: validDegreesFor(data.left_degrees, isPostTypeT),
          left_depth_mm: isPostTypeT ? undefined : (data.left_depth_mm ?? -3),
        });
      } else {
        // None / undefined: clear the dependent fields too so they don't
        // resurface in summaries or get submitted with stale values.
        update({
          left_type: v,
          left_correction: undefined,
          left_degrees: undefined,
          left_depth_mm: undefined,
        });
      }
    } else {
      if (v && v !== 'none') {
        update({
          right_type: v,
          right_correction: data.right_correction ?? 'medial',
          right_degrees: validDegreesFor(data.right_degrees, isPostTypeT),
          right_depth_mm: isPostTypeT ? undefined : (data.right_depth_mm ?? -3),
        });
      } else {
        update({
          right_type: v,
          right_correction: undefined,
          right_degrees: undefined,
          right_depth_mm: undefined,
        });
      }
    }
  };

  // Wedge types are available on both sides regardless of the rearfoot posting
  // Yes/No flag; the lab handles the EVA post as a glued-on piece when no
  // rearfoot post is built. Per-shell + sandal availability lives in
  // rearfootWedgeRules (shared with the unit tests).
  const allowedTypes = rearfootWedgeTypesFor(subtype, !!isSandal);
  const typeOptions = REARFOOT_WEDGE_TYPES.filter((t) =>
    allowedTypes.includes(t.value),
  );

  const setCorrection = (v: RearfootWedgeCorrection | undefined) =>
    update(isLeft ? { left_correction: v } : { right_correction: v });
  const setDegrees = (v: number | undefined) =>
    update(isLeft ? { left_degrees: v } : { right_degrees: v });
  const setDepth = (v: number | undefined) =>
    update(isLeft ? { left_depth_mm: v } : { right_depth_mm: v });

  return (
    <SideRow side={side}>
      <SelectSm
        value={type}
        onChange={setType}
        options={typeOptions}
        disabled={readOnly}
        placeholder="Type"
        className="min-w-[12rem]"
        // The checker normalises the pre-R34 enum before judging, and so does
        // `type` above, so both are asking about the same value.
        unavailableLabel={
          unavailable?.[`rearfoot_wedge_${isLeft ? 'left' : 'right'}_type`]?.valueLabel
        }
        invalid={!!unavailable?.[`rearfoot_wedge_${isLeft ? 'left' : 'right'}_type`]}
      />
      {type && type !== 'none' ? (
        <>
          <SelectSm
            value={correction}
            onChange={setCorrection}
            options={REARFOOT_WEDGE_CORRECTIONS}
            disabled={readOnly}
            placeholder="Correction"
            className="min-w-[15rem]"
          />
          <NumInput
            value={degrees}
            onChange={setDegrees}
            min={isPostType ? REARFOOT_POST_ANGLE.min : REARFOOT_SKIVE_ANGLE.min}
            max={isPostType ? REARFOOT_POST_ANGLE.max : REARFOOT_SKIVE_ANGLE.max}
            disabled={readOnly}
            placeholder="Angle"
            suffix="°"
            width="w-20"
          />
          {/* Depth is hidden for the EVA post (extrinsic) and the intrinsic
              rearfoot post: post thickness is set by the angle, not a separate
              skive depth. Only Kirby skive carries a depth. */}
          {!hidesDepth ? (
            <NumInput
              value={depth}
              onChange={setDepth}
              min={-6}
              max={0}
              disabled={readOnly}
              placeholder="Depth"
              suffix="mm"
              width="w-24"
            />
          ) : null}
        </>
      ) : null}
    </SideRow>
  );
}

// ---- Subcalcaneal Offloading Card ---------------------------------------

function SubcalcanealOffloadingCard({
  value,
  onChange,
  readOnly,
  isSandal,
  unavailable,
}: {
  value: {
    left?: SubcalcanealOffloadingSide | undefined;
    right?: SubcalcanealOffloadingSide | undefined;
  };
  onChange: (v: {
    left?: SubcalcanealOffloadingSide | undefined;
    right?: SubcalcanealOffloadingSide | undefined;
  }) => void;
  readOnly?: boolean | undefined;
  isSandal?: boolean | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  const patchSide = (side: 'left' | 'right', p: Partial<SubcalcanealOffloadingSide>) => {
    const prev = (side === 'left' ? value.left : value.right) ?? {};
    onChange({ ...value, [side]: { ...prev, ...p } });
  };

  return (
    <div
      id={CARD_IDS.subcalcanealOffloading}
      className="rounded-md border border-border-subtle bg-white p-3 scroll-mt-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="space-y-2 min-w-0">
          <div>
            <div className="text-sm font-semibold text-text-primary leading-tight">
              Subcalcaneus offloading
            </div>
            <div className="text-[11px] text-text-muted mt-0.5">
              {isSandal
                ? 'Pick fill + depth/dimension per side.'
                : 'Heel offloading or focal cutout. Pick fill + depth/dimension per side.'}
            </div>
          </div>
          <SubcalRow side="left" data={value.left ?? {}} onChange={(p) => patchSide('left', p)} readOnly={readOnly} isSandal={isSandal} unavailable={unavailable} />
          <SubcalRow side="right" data={value.right ?? {}} onChange={(p) => patchSide('right', p)} readOnly={readOnly} isSandal={isSandal} unavailable={unavailable} />
        </div>
        <div className="hidden lg:flex items-center justify-center w-16 h-16 rounded-md bg-sand-50 shrink-0 overflow-hidden self-start">
          <img
            src="/prescription/modifications/subcalcaneal-offloading.png"
            alt="Subcalcaneal offloading reference"
            className="h-full w-full object-contain p-1"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

function SubcalRow({
  side,
  data,
  onChange,
  readOnly,
  isSandal,
  unavailable,
}: {
  side: 'left' | 'right';
  data: SubcalcanealOffloadingSide;
  onChange: (p: Partial<SubcalcanealOffloadingSide>) => void;
  readOnly?: boolean | undefined;
  isSandal?: boolean | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  // Sandal mode (R37) hides the focal heel_cutout option. Rule shared with the
  // legality checker via ./heelRaiseRules.
  const allowedOffloadingTypes = subcalOffloadingTypesFor(!!isSandal);
  const offloadingTypeOptions = SUBCAL_OFFLOADING_TYPES.filter((t) =>
    allowedOffloadingTypes.includes(t.value),
  );
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="w-4 text-xs font-mono text-text-muted">{side === 'left' ? 'L' : 'R'}</span>
      <SelectSm
        value={data.type}
        onChange={(v) => {
          // Switching type clears fields that don't apply.
          if (v === 'heel_offloading') {
            onChange({
              type: v,
              fill: data.fill ?? 'carbosan_3mm',
              depth_mm: data.depth_mm ?? -3,
              dimension: undefined,
              position: undefined,
            });
          } else if (v === 'heel_cutout') {
            onChange({
              type: v,
              fill: data.fill ?? 'none',
              depth_mm: undefined,
              dimension: data.dimension ?? 'medium',
              position: data.position ?? 'center',
            });
          } else {
            onChange({
              type: v,
              fill: undefined,
              depth_mm: undefined,
              dimension: undefined,
              position: undefined,
            });
          }
        }}
        options={offloadingTypeOptions}
        disabled={readOnly}
        placeholder="Offloading type"
        className="min-w-[9rem]"
        unavailableLabel={unavailable?.[`subcalcaneal_offloading_${side}_type`]?.valueLabel}
        invalid={!!unavailable?.[`subcalcaneal_offloading_${side}_type`]}
      />
      {data.type === 'heel_offloading' || data.type === 'heel_cutout' ? (
        <SelectSm
          value={data.fill}
          onChange={(v) => onChange({ fill: v })}
          options={SUBCAL_FILL_OPTIONS}
          disabled={readOnly}
          placeholder="Fill"
          className="min-w-[9rem]"
        />
      ) : null}
      {data.type === 'heel_offloading' ? (
        <NumInput
          value={data.depth_mm}
          onChange={(v) => onChange({ depth_mm: v })}
          min={-6}
          max={-1}
          step={1}
          disabled={readOnly}
          placeholder="Depth"
          suffix="mm"
          width="w-24"
        />
      ) : null}
      {data.type === 'heel_cutout' ? (
        <SelectSm
          value={data.dimension}
          onChange={(v) => onChange({ dimension: v })}
          options={HEEL_CUTOUT_DIMENSIONS}
          disabled={readOnly}
          placeholder="Dimension"
          className="min-w-[7rem]"
        />
      ) : null}
      {data.type === 'heel_cutout' ? (
        <SelectSm
          value={data.position}
          onChange={(v) => onChange({ position: v })}
          options={SUBCAL_POSITION_OPTIONS}
          disabled={readOnly}
          placeholder="Position"
          className="min-w-[6rem]"
        />
      ) : null}
    </div>
  );
}

// ---- Yes/No toggle -------------------------------------------------------

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean | undefined;
  onChange: (next: boolean) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <div className="inline-flex gap-1">
      {[
        { v: true, label: 'Yes' },
        { v: false, label: 'No' },
      ].map((opt) => (
        <button
          key={String(opt.v)}
          type="button"
          onClick={() => !disabled && onChange(opt.v)}
          disabled={disabled}
          className={cn(
            'px-3 py-1 rounded-md border text-xs transition',
            value === opt.v
              ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
              : 'border-border-subtle hover:border-brand-300',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
